<template lang="pug">

  v-app(full-height)
    LFCustomTooltipComponent
    LFToastsContainerComponent
    SpinnerComponent
    SpinnerLayerComponent(
      v-if="showSpinnerLayer"
      class="spinner-layer-component"
    )
    DialogComponent
    CustomDialogComponent
    v-main(:class="{'remove-padding': removeMainPadding, 'is-mobile': renderMobileViews}")
      div(class="alerts-container")
        AlertComponent(
          :whereIsRendered="componentWhereIsRendered"
        )
      v-container(fluid pa-0 class="app-container")
        NavigationContainerComponent(
          v-if="!hideNavigation && renderNavigationGetter"
          :isFooterVisible="isFooterVisible"
          :drawerHeight="drawerHeight"
        )
        router-view(:key="ckeckIfReloadURL")
    LandbotLoader(v-if="showLandbot")
    LefebvreFooterComponent(v-show="showFooter" ref="footer")

  </template>

<script lang="ts">
import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { to } from '@/router/types/routerTypes'
import { URLS } from '@/router/routes/urlRoutes'
import { Getter, Mutation } from 'vuex-class'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import CustomDialogComponent from '@/components/Dialog/CustomDialog/CustomDialogComponent.vue'
import DialogComponent from '@/components/Dialog/DialogComponent.vue'
import LefebvreFooterComponent from '@/components/Footer/LefebvreFooterComponent.vue'
import NavigationContainerComponent from '@/components/Navigation/NavigationContainer/NavigationContainerComponent.vue'
import SpinnerComponent from '@/components/Spinner/SpinnerComponent.vue'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import AlertComponent from '@/components/Alert/AlertComponent.vue'
import { ComponentWhereIsRendered } from '@/store/modules/alerts/alertsTypes'
import LFToastsContainerComponent from '@/components/Toasts/LFToastsContainerComponent.vue'
import LFCustomTooltipComponent from '@/components/Tooltip/LFCustomTooltipComponent.vue'
import LandbotLoader from '@/components/landbot/LandbotLoader.vue'
import { MOBILE_URLS } from '@/router/routes/urlMobileRoutes'
import { checkIsMobile } from '@/helpers/navigator'

const expedientsModule = ModuleNamespaces.EXPEDIENTS
const spinnerModule = ModuleNamespaces.SPINNER
const menusModule = ModuleNamespaces.MENUS

@Component({
  components: {
    AlertComponent,
    CustomDialogComponent,
    DialogComponent,
    LandbotLoader,
    LFCustomTooltipComponent,
    LFToastsContainerComponent,
    LefebvreFooterComponent,
    NavigationContainerComponent,
    SpinnerComponent,
    SpinnerLayerComponent
  }
})
export default class App extends Vue {
  @Getter('getDuplicateExpedientId', { namespace: expedientsModule })
  duplicateExpedientId: number

  @Getter('getShowSpinnerLayer', { namespace: spinnerModule })
  showSpinnerLayer: boolean

  @Getter('getDrawerStatus', { namespace: menusModule })
  drawerInMiniStatus: boolean

  @Getter('getRenderNavigation', { namespace: menusModule })
  renderNavigationGetter: boolean

  @Getter('getIsMobileUser', { namespace: ModuleNamespaces.AUTH })
  isMobileUser: boolean

  @Getter('userIsAuthenticated', { namespace: ModuleNamespaces.AUTH })
  isUserAuthenticated: boolean

  @Getter('getShowLandbot', { namespace: ModuleNamespaces.AUTH })
  showLandbot: boolean

  @Mutation('RENDER_NAVIGATION', { namespace: menusModule })
  renderNavigationMutation: () => []

  isFooterVisible = false

  componentWhereIsRendered = ComponentWhereIsRendered.MAIN

  scrollListenerAdded = false

  drawerHeight = '100%'

  @Watch('showSpinnerLayer')
  async checkFooterStatus() {
    if (this.showFooter) {
      await this.$nextTick()
      this.handleScroll()
    } else {
      this.drawerHeight = '100%'
    }
  }

  @Watch('$route')
  async onRouteChange(to: to) {
    if (to.name !== URLS.LOGIN && this.renderNavigationGetter === false) {
      this.renderNavigationMutation()
    }
  }

  get hideNavigation(): boolean {
    return [
      URLS.AGENDA,
      URLS.EMAIL,
      `${URLS.EMAIL}-${URLS.EDIT}`,
      `${URLS.EMAIL}-${URLS.ACTIONS}`,
      `${URLS.EMAIL}-${URLS.NEW}`,
      URLS.DOUBLE_OPTIN,
      URLS.LEXNET,
      URLS.LOGIN,
      URLS.NAVISION_LOGIN,
      URLS.NOT_FOUND_PAGE,
      URLS.REMEMBER_PASSWORD,
      URLS.SELECT_COMPANY,
      `${URLS.ANOTHER_APPLICATIONS}-${URLS.SSO_PAYMENTS}`,
      `${URLS.ANOTHER_APPLICATIONS}-${URLS.SSO_UNPAID}`,
      URLS.ONLINE,
      // TODO FIGMA: Eliminar rutas temporales
      '/label-buttons',
      '/icon-buttons',
      '/counters'
    ].includes(this.$route.name!)  || this.renderMobileViews
  }

  get ckeckIfReloadURL() {
    if (
      this.$route.name === `${URLS.EXPEDIENTS}-${URLS.EXPEDIENTS}` &&
      (this.$route.query.action || this.duplicateExpedientId)
    ) {
      return Number(this.$route.query)
    }
    return 0
  }

  get isDashboardView() {
    return this.$route.name === URLS.DASHBOARD
  }

  get showFooter() {
    const url = this.$route.name || ''
    return [URLS.DASHBOARD, URLS.COOKIES_POLICY].includes(url)
  }

  get isViewWithoutPadding() {
    const views = [URLS.LOGIN, URLS.SELECT_COMPANY, MOBILE_URLS.HOME]
    return views.includes(this.$route.name!)
  }

  get removeMainPadding() {
    return this.isViewWithoutPadding && this.renderMobileViews
  }

  get renderMobileViews() {
    return this.isMobileUser || (!this.isUserAuthenticated && checkIsMobile())
  }

  updated() {
    this.updateScrollListener()
  }

  updateScrollListener() {
    if (this.showFooter) {
      if (!this.scrollListenerAdded) {
        window.addEventListener('scroll', this.handleScroll)
        this.scrollListenerAdded = true
      }
    } else {
      this.isFooterVisible = false
      if (this.scrollListenerAdded) {
        window.removeEventListener('scroll', this.handleScroll)
        this.scrollListenerAdded = false
      }
    }
  }

  handleScroll() {
    const footer = this.$refs.footer

    if (footer && this.showFooter) {
      const footerRect = (footer as any).$el.getBoundingClientRect()
      this.isFooterVisible = footerRect.top < window.innerHeight
      // Calculamos la altura del menú dinamicamente
      this.isFooterVisible ? (this.drawerHeight = `${footerRect.top}px`) : (this.drawerHeight = '100%')
    }
  }
}
</script>

<style lang="scss"></style>
